import type { UpdateCustomRoleMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

export interface UpdateCustomRoleTRPCInput {
  id: string;
  name: string;
  description?: string | null;
  roleKeys: string[];
  userIds: string[];
}

/**
 * Maps the tRPC `customRoleUpdate` response (camelCase `affectedRows`)
 * back into the Apollo `UpdateCustomRoleMutation` shape (snake_case
 * `affected_rows`) so call sites can keep checking
 * `result.customRoleUpdate?.affected_rows !== 1`.
 */
const mapTrpcResponseToGraphQL = (trpcData: {
  affectedRows: number;
}): UpdateCustomRoleMutation => ({
  customRoleUpdate: {
    __typename: 'GenericMutationOutput',
    affected_rows: trpcData.affectedRows,
  },
});

export const useUpdateCustomRoleTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.customRole.customRoleUpdate.mutationOptions({
      onSuccess: async (_data, variables) => {
        // Invalidate the list family and the per-id detail query the
        // custom-role settings page now reads via `useGetCustomRoleById`
        // (tRPC `customRoleById`), so the page reflects the save.
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.role.getCustomRoles.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.customRole.customRoleById.queryKey({
              id: variables.id,
            }),
          }),
        ]);
      },
    }),
  });

  return {
    updateCustomRole: async (
      variables: UpdateCustomRoleTRPCInput
    ): Promise<UpdateCustomRoleMutation> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
